import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import { canManageUsersInVisitorGroup } from 'v2/utils/visitorGroupOrigin';

/** The parts of a group the modal reads; the slim groups query only selects the member ids. */
export type AddUsersToGroupsVisitorGroup = Pick<
  VisitorGroup,
  'id' | 'name' | 'type' | 'memberIds' | 'maxNumberOfMembers'
>;

export const AddUsersToGroupsSelectionState = {
  Available: 'available',
  AlreadyAssigned: 'alreadyAssigned',
  LimitReached: 'limitReached'
} as const;

export type AddUsersToGroupsSelectionState =
  (typeof AddUsersToGroupsSelectionState)[keyof typeof AddUsersToGroupsSelectionState];

export interface GroupSelectionState {
  state: AddUsersToGroupsSelectionState;
  selectedUserIdsAlreadyInGroup: string[];
}

export interface SelectAllState {
  checked: boolean;
  indeterminate: boolean;
}

export const getGroupSelectionState = (
  visitorGroup: AddUsersToGroupsVisitorGroup,
  selectedUserIds: string[]
): GroupSelectionState => {
  const memberIds = new Set(visitorGroup.memberIds);
  const selectedUserIdsAlreadyInGroup = selectedUserIds.filter((visitorId) => memberIds.has(visitorId));
  const usersToAddCount = selectedUserIds.length - selectedUserIdsAlreadyInGroup.length;

  if (usersToAddCount === 0) {
    return { state: AddUsersToGroupsSelectionState.AlreadyAssigned, selectedUserIdsAlreadyInGroup };
  }

  // A full group has no capacity left, so it is covered by the same check as a group too small for the selection.
  const remainingCapacity =
    visitorGroup.maxNumberOfMembers == null ? undefined : visitorGroup.maxNumberOfMembers - memberIds.size;

  if (remainingCapacity !== undefined && remainingCapacity < usersToAddCount) {
    return { state: AddUsersToGroupsSelectionState.LimitReached, selectedUserIdsAlreadyInGroup };
  }

  return { state: AddUsersToGroupsSelectionState.Available, selectedUserIdsAlreadyInGroup };
};

export const getSelectableGroupIds = (visitorGroups: AddUsersToGroupsVisitorGroup[], selectedUserIds: string[]) =>
  visitorGroups
    .filter(canManageUsersInVisitorGroup)
    .filter(
      (visitorGroup) =>
        getGroupSelectionState(visitorGroup, selectedUserIds).state === AddUsersToGroupsSelectionState.Available
    )
    .map((visitorGroup) => visitorGroup.id);

export const getSelectAllState = (visibleSelectableGroupIds: string[], selectedGroupIds: string[]): SelectAllState => {
  const selectedGroupIdSet = new Set(selectedGroupIds);
  const selectedCount = visibleSelectableGroupIds.filter((groupId) => selectedGroupIdSet.has(groupId)).length;

  return {
    checked: visibleSelectableGroupIds.length > 0 && selectedCount === visibleSelectableGroupIds.length,
    indeterminate: selectedCount > 0 && selectedCount < visibleSelectableGroupIds.length
  };
};

export const toggleVisibleSelectableGroupIds = (
  selectedGroupIds: string[],
  visibleSelectableGroupIds: string[]
): string[] => {
  const { checked } = getSelectAllState(visibleSelectableGroupIds, selectedGroupIds);

  return checked
    ? selectedGroupIds.filter((groupId) => !visibleSelectableGroupIds.includes(groupId))
    : [...selectedGroupIds, ...visibleSelectableGroupIds.filter((groupId) => !selectedGroupIds.includes(groupId))];
};
